import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../config/api';
import { PatientStatus, buildDisplayName } from '../types';
import type { IntakeConversionContext } from '../components/calendar/types';
import { useThemedStyles } from '../theme/useThemedStyles';
import { formatDate } from '../utils/dateFormat';
import { BolivianDateInput } from '../components/common/BolivianDateInput';

interface CreatedPatientSummary {
  id: number;
  name: string;
  email?: string | null;
  nextContact?: string | null;
  hasCriticalAllergy?: boolean;
  noShowCount?: number;
}

interface CreatePatientProps {
  token: string;
  onCancel: () => void;
  onOpenProfile?: (id: number) => void;
  onPatientCreated?: (patient: CreatedPatientSummary, intakeContext?: IntakeConversionContext | null) => void;
  intakeContext?: IntakeConversionContext | null;
}

interface PatientFormState {
  hc: string;
  firstName: string;
  secondName: string;
  firstLastName: string;
  secondLastName: string;
  presentedName: string;
  email: string;
  dateOfBirth: string;
  nextContact: string;
  phoneNumber: string;
  address: string;
  status: PatientStatus;
  notes: string;
  profilePicturePath: string;
  referralSource: string;
  responsiblePartyId: string;
  isForeignPatient: boolean;
}

const createEmptyFormState = (): PatientFormState => ({
  hc: '',
  firstName: '',
  secondName: '',
  firstLastName: '',
  secondLastName: '',
  presentedName: '',
  email: '',
  dateOfBirth: '',
  nextContact: '',
  phoneNumber: '',
  address: '',
  status: 'New',
  notes: '',
  profilePicturePath: '',
  referralSource: '',
  responsiblePartyId: '',
  isForeignPatient: false
});

const buildInitialFormState = (intake?: IntakeConversionContext | null): PatientFormState => {
  const base = createEmptyFormState();
  if (!intake) return base;
  const next = { ...base };

  if (intake.prospectName && intake.prospectName.trim().length) {
    const prospectName = intake.prospectName.trim();
    const parts = prospectName.split(/\s+/);
    if (parts.length > 0) {
      next.firstName = parts[0];
    }
    if (parts.length > 1) {
      next.firstLastName = parts[parts.length - 1];
    }
    if (parts.length > 2) {
      next.secondName = parts[1];
    }
    if (parts.length > 3) {
      next.secondLastName = parts[parts.length - 2];
    }
    next.presentedName = prospectName;
  }

  if (intake.prospectContact && intake.prospectContact.trim().length) {
    const contact = intake.prospectContact.trim();
    if (contact.includes('@')) {
      next.email = contact;
    } else {
      next.phoneNumber = contact;
    }
  }

  const collectedNotes = [
    intake.prospectNotes && intake.prospectNotes.trim().length ? intake.prospectNotes.trim() : null,
    intake.notes && intake.notes.trim().length ? intake.notes.trim() : null
  ].filter((entry): entry is string => !!entry);
  if (collectedNotes.length > 0) {
    next.notes = collectedNotes.join('\n');
  }

  return next;
};

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  border: '1px solid #334155',
  borderRadius: 6,
  fontSize: 14,
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
  display: 'block',
  background: '#0f172a',
  color: '#f1f5f9'
};

const sectionStyle: React.CSSProperties = {
  background: '#1e293b',
  borderRadius: 12,
  padding: 24,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  color: '#f1f5f9'
};

const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#94a3b8' };

const gridStyle = (minWidth = 220): React.CSSProperties => ({
  display: 'grid',
  gap: 12,
  width: '100%',
  gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}px, 1fr))`
});

export const CreatePatient: React.FC<CreatePatientProps> = ({ token, onCancel, onOpenProfile, onPatientCreated, intakeContext }) => {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [form, setForm] = useState<PatientFormState>(() => buildInitialFormState(intakeContext));
  const intakeEventId = intakeContext?.eventId ?? null;

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [createdPatientId, setCreatedPatientId] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isCompact, setIsCompact] = useState(false);
  const [suggestingHC, setSuggestingHC] = useState(false);

  useEffect(() => {
    if (!intakeContext) return;
    setForm(buildInitialFormState(intakeContext));
    setCreatedPatientId(null);
    setSuccessMessage('');
    setError('');
  }, [intakeContext, intakeEventId]);

  useEffect(() => {
    const updateSize = () => {
      if (typeof window === 'undefined') return;
      setIsCompact(window.innerWidth < 1100);
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const suggestNextHC = useCallback(async () => {
    setSuggestingHC(true);
    try {
      const res = await fetch(
        getApiUrl(`/patients/next-hc${form.isForeignPatient ? '?foreign=true' : ''}`),
        {
        headers: getAuthHeaders(token)
        }
      );
      if (!res.ok) throw new Error('Failed to fetch next HC');
      const data = await res.json();
      if (data.nextHC) {
        setForm(prev => ({ ...prev, hc: String(data.nextHC) }));
      }
    } catch (err) {
      console.error('Failed to fetch next HC:', err);
      setError(t('createPatient.hcSuggestionError'));
    } finally {
      setSuggestingHC(false);
    }
  }, [form.isForeignPatient, t, token]);

  useEffect(() => {
    if (form.hc || createdPatientId || form.status === 'Responsible') return;
    void suggestNextHC();
  }, [createdPatientId, form.hc, form.status, intakeEventId, suggestNextHC]);

  const layoutStyle = useMemo<React.CSSProperties>(() => ({
    display: 'grid',
    gap: 24,
    alignItems: 'start',
    gridTemplateColumns: isCompact ? 'minmax(0, 1fr)' : 'minmax(0, 3fr) minmax(320px, 1fr)'
  }), [isCompact]);
  const themedInputStyle: React.CSSProperties = { ...inputStyle, ...theme.input };
  const themedSectionStyle: React.CSSProperties = { ...sectionStyle, ...theme.card, boxShadow: theme.shadow };
  const themedLabelStyle: React.CSSProperties = { ...labelStyle, color: theme.muted };

  const intakeTimeSummary = useMemo(() => {
    if (!intakeContext) return null;
    try {
      const start = new Date(intakeContext.startTime);
      const end = new Date(intakeContext.endTime);
      const dateLabel = formatDate(start);
      const startLabel = start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      const endLabel = end.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      return `${dateLabel} • ${startLabel} - ${endLabel}`;
    } catch {
      return null;
    }
  }, [intakeContext]);

  const liveDisplayName = useMemo(() => buildDisplayName({
    firstName: form.firstName,
    secondName: form.secondName,
    firstLastName: form.firstLastName,
    secondLastName: form.secondLastName,
    presentedName: form.presentedName
  }), [form.firstName, form.secondName, form.firstLastName, form.secondLastName, form.presentedName]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setForm(prev => ({ ...prev, [name]: checked }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    setSuccessMessage('');
    try {
      if (form.status !== 'Responsible' && !form.hc.trim()) {
        throw new Error(t('createPatient.hcRequired'));
      }
      const payload = {
        hc: form.hc.trim() || null,
        firstName: form.firstName,
        secondName: form.secondName || null,
        firstLastName: form.firstLastName,
        secondLastName: form.secondLastName || null,
        presentedName: liveDisplayName,
        email: form.email || '',
        dateOfBirth: form.dateOfBirth || null,
        nextContact: form.nextContact || null,
        phoneNumber: form.phoneNumber || '',
        address: form.address || '',
        status: form.status,
        notes: form.notes || '',
        profilePicturePath: form.profilePicturePath || null,
        referralSource: form.referralSource || '',
        responsiblePartyId: form.responsiblePartyId ? Number(form.responsiblePartyId) : null,
        isForeignPatient: form.isForeignPatient
      };
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENTS), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const message = await res.text();
        throw new Error(message || 'Failed to create patient');
      }
      const data = await res.json();
      const createdId = typeof data?.id === 'number' ? data.id : null;
      if (createdId !== null) {
        setCreatedPatientId(createdId);
        const displayName = buildDisplayName({
          firstName: data?.firstName,
          secondName: data?.secondName,
          firstLastName: data?.firstLastName,
          secondLastName: data?.secondLastName,
          presentedName: data?.presentedName
        }) || `Patient ${createdId}`;
        const summary: CreatedPatientSummary = {
          id: createdId,
          name: displayName,
          email: data?.email ?? null,
          nextContact: data?.nextContact ?? null,
          hasCriticalAllergy: !!data?.hasCriticalAllergy,
          noShowCount: typeof data?.noShowCount === 'number' ? data.noShowCount : 0
        };
        if (onPatientCreated) {
          onPatientCreated(summary, intakeContext);
        }
      } else {
        setCreatedPatientId(null);
      }
      setSuccessMessage(intakeContext ? t('createPatient.successIntake') : t('createPatient.successGeneric'));
      setForm(createEmptyFormState());
    } catch (err: any) {
      setError(err.message || t('createPatient.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="responsive-grid" style={layoutStyle}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={themedSectionStyle}>
          <div>
            <h2 style={{ margin: 0 }}>{intakeContext ? t('createPatient.registerIntake') : t('createPatient.title')}</h2>
            <p style={{ margin: '6px 0 0', color: theme.muted, fontSize: 14 }}>
              {intakeContext
                ? t('createPatient.intakeDescription')
                : t('createPatient.description')}
            </p>
            {intakeContext && (
              <div style={{ marginTop: 12, background: '#451a03', border: '1px solid #b45309', borderRadius: 10, padding: '12px 16px', color: '#fef3c7', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {intakeTimeSummary && <div><strong>{t('createPatient.intakeSlot')}:</strong> {intakeTimeSummary}</div>}
                {intakeContext.prospectContact && <div><strong>{t('createPatient.contactOnFile')}:</strong> {intakeContext.prospectContact}</div>}
                {intakeContext.prospectNotes && <div><strong>{t('createPatient.intakeNotes')}:</strong> {intakeContext.prospectNotes}</div>}
              </div>
            )}
          </div>
          <div style={gridStyle(200)}>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.firstName')} *</div>
              <input
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                style={themedInputStyle}
                placeholder="María"
                required
              />
            </div>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.secondName')}</div>
              <input
                name="secondName"
                value={form.secondName}
                onChange={handleChange}
                style={themedInputStyle}
                placeholder="José"
              />
            </div>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.firstLastName')} *</div>
              <input
                name="firstLastName"
                value={form.firstLastName}
                onChange={handleChange}
                style={themedInputStyle}
                placeholder="González"
                required
              />
            </div>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.secondLastName')}</div>
              <input
                name="secondLastName"
                value={form.secondLastName}
                onChange={handleChange}
                style={themedInputStyle}
                placeholder="Ruiz"
              />
            </div>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.displayName')}</div>
              <input
                name="presentedName"
                value={form.presentedName}
                onChange={handleChange}
                style={themedInputStyle}
                placeholder={t('createPatient.displayNamePlaceholder')}
              />
            </div>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.hc')}</div>
              <input
                name="hc"
                value={form.hc}
                onChange={handleChange}
                style={themedInputStyle}
                placeholder={t('createPatient.hcPlaceholder')}
                required={form.status !== 'Responsible'}
              />
              <div style={{ marginTop: 6, color: theme.muted, fontSize: 12 }}>
                {form.isForeignPatient
                  ? t('createPatient.internationalHcHelp')
                  : t('createPatient.hcHelp')}
              </div>
              <button
                type="button"
                onClick={suggestNextHC}
                disabled={suggestingHC}
                style={{
                  marginTop: 8,
                  padding: '7px 10px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  background: theme.surfaceAlt,
                  color: theme.text,
                  cursor: suggestingHC ? 'not-allowed' : 'pointer'
                }}
              >
                {suggestingHC ? t('createPatient.suggestingHc') : t('createPatient.suggestNextHc')}
              </button>
            </div>
          </div>
        </div>

        <div style={themedSectionStyle}>
          <h3 style={{ margin: 0 }}>{t('createPatient.contactStatus')}</h3>
          <div style={gridStyle(210)}>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.email')}</div>
              <input
                name="email"
                value={form.email}
                onChange={handleChange}
                style={themedInputStyle}
                type="email"
                placeholder="maria@example.com"
              />
            </div>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.phone')}</div>
              <input
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                style={themedInputStyle}
                placeholder="+593 99 123 4567"
              />
            </div>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.address')}</div>
              <input
                name="address"
                value={form.address}
                onChange={handleChange}
                style={themedInputStyle}
                placeholder="Street, City, Country"
              />
            </div>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.status')} *</div>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                style={{ ...themedInputStyle, paddingRight: 32 }}
                required
              >
                <option value="New">{t('createPatient.statusPending')}</option>
                <option value="Active">{t('createPatient.statusActive')}</option>
                <option value="Inactive">{t('createPatient.statusInactive')}</option>
                <option value="Archived">{t('createPatient.statusArchived')}</option>
                <option value="Responsible">{t('createPatient.statusResponsible')}</option>
              </select>
            </div>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.referralSource')}</div>
              <input
                name="referralSource"
                value={form.referralSource}
                onChange={handleChange}
                style={themedInputStyle}
                placeholder={t('createPatient.referralSourcePlaceholder')}
              />
            </div>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.responsiblePartyId')}</div>
              <input
                name="responsiblePartyId"
                value={form.responsiblePartyId}
                onChange={handleChange}
                style={themedInputStyle}
                placeholder={t('createPatient.responsiblePartyIdPlaceholder')}
              />
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <label className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="isForeignPatient"
                checked={form.isForeignPatient}
                onChange={event => {
                  const checked = event.target.checked;
                  setForm(previous => ({ ...previous, isForeignPatient: checked, hc: '' }));
                }}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 14, fontWeight: 500, color: theme.text }}>
                {t('createPatient.isForeignPatient')}
              </span>
            </label>
          </div>
          <div style={gridStyle(210)}>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.dateOfBirth')}</div>
              <BolivianDateInput name="dateOfBirth" value={form.dateOfBirth} onChange={dateOfBirth => setForm(previous => ({ ...previous, dateOfBirth }))} style={themedInputStyle} />
            </div>
            <div>
              <div style={themedLabelStyle}>{t('createPatient.nextContact')}</div>
              <BolivianDateInput name="nextContact" value={form.nextContact} onChange={nextContact => setForm(previous => ({ ...previous, nextContact }))} style={themedInputStyle} />
            </div>
          </div>
        </div>

        <div style={themedSectionStyle}>
          <h3 style={{ margin: 0 }}>{t('createPatient.notesMedia')}</h3>
          <div>
            <div style={themedLabelStyle}>{t('createPatient.notes')}</div>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              style={{ ...themedInputStyle, minHeight: 120, resize: 'vertical' }}
              placeholder={t('createPatient.notesPlaceholder')}
            />
          </div>
          <div>
            <div style={themedLabelStyle}>{t('createPatient.profilePhotoPath')}</div>
            <input
              name="profilePicturePath"
              value={form.profilePicturePath}
              onChange={handleChange}
              style={themedInputStyle}
              placeholder="/uploads/patients/..."
            />
          </div>
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '12px 16px', borderRadius: 8 }}>
            {error}
          </div>
        )}
        {successMessage && (
          <div style={{ background: '#dcfce7', color: '#166534', padding: '12px 16px', borderRadius: 8 }}>
            {successMessage}
          </div>
        )}

        <div className="responsive-row" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="submit"
            style={{
              padding: '12px 24px',
              background: submitting ? theme.surfaceAlt : theme.primary,
              color: theme.primaryText,
              border: `1px solid ${submitting ? theme.border : theme.primary}`,
              borderRadius: 8,
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: 600
            }}
            disabled={submitting}
          >
            {submitting ? t('createPatient.saving') : t('createPatient.createButton')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '12px 20px',
              background: theme.surfaceAlt,
              border: `1px solid ${theme.border}`,
              borderRadius: 8,
              color: theme.text,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {intakeContext ? t('createPatient.backToCalendar') : t('createPatient.backToList')}
          </button>
          {createdPatientId && onOpenProfile && (
            <button
              type="button"
              onClick={() => onOpenProfile(createdPatientId)}
              style={{
                padding: '12px 20px',
                background: '#047857',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {t('createPatient.viewProfile')}
            </button>
          )}
        </div>
      </form>

      <aside style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ ...themedSectionStyle, position: 'sticky', top: 24 }}>
          <h3 style={{ margin: 0 }}>{t('createPatient.previewCard')}</h3>
          <div style={{ background: theme.inputBg, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{liveDisplayName || t('createPatient.newPatient')}</div>
            <div style={{ fontSize: 13, color: theme.muted }}>{form.email || t('createPatient.noEmail')}</div>
            <div style={{ fontSize: 13, color: theme.muted }}>{form.phoneNumber || t('createPatient.noPhone')}</div>
            <div style={{ fontSize: 12, marginTop: 8, color: theme.text }}>{form.notes ? form.notes.slice(0, 140) : t('createPatient.notesPreview')}</div>
          </div>
          <ul style={{ margin: 0, padding: '0 0 0 16px', color: theme.muted, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <li>{t('createPatient.previewTip1')}</li>
            <li>{t('createPatient.previewTip2')}</li>
            <li>{t('createPatient.previewTip3')}</li>
          </ul>
        </div>
      </aside>
    </div>
  );
};

export default CreatePatient;


